// The notes written on a contact's PROPERTIES, shown on the contact's card — the seller and his
// property as ONE thing (Trello #254 follow-up, 2026-11-04).
//
// A realtor working the listings board writes her call notes on the PROPERTY (the board's quick
// notes — `user-notes/{email}__listing__{listingId}`), and the owner she saved from that property
// is a separate CONTACT with its own notes document. Nothing read one from the other, so the
// support rep's test — a note on the property, then the owner's card — found nothing: "the notes
// we put on the property from the table are not saved on the contact". The customer asked for
// what her previous CRM did: one record for the seller and his property.
//
// So the card reads the notes of every listing the contact is linked to as its seller / landlord
// (`sellingListingIds` / `rentingOutListingIds` — the link the board's save writes), own and
// office-shared, exactly as the property's own popover reads them (`useListingNotes`), and shows
// them beside the contact's notes, labelled with the property. Read, never copied: one note, one
// place, so it is edited where it was written and nothing drifts — and the notes she wrote before
// this existed show up too.
import { useEffect, useMemo, useState } from "react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import { setCached } from "../../data/sessionCache";
import { parseNoteTime } from "./notesData";
import { buildPropertyNotes, linkedListingIdsOf, propertyLabelOf, propertyNotesCacheKey } from "./propertyNotes";
import type { LinkedListings } from "./propertyNotes";
import { USER_NOTES_COLLECTION_NAME, userNotesDocId } from "../../../../../../@types/sharedSchema";
import type { RealEstateContact, UserNote, UserNotesDocument } from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";

/**
 * Live notes of the properties this contact is linked to (see the header). Mirrors the result
 * into `property-notes:{contactId}` for the notes tab's badge.
 */
export function usePropertyNotes(
  contact: (LinkedListings & Pick<RealEstateContact, "id">) | null | undefined
): DisplayUserNote[] {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, organizationUsers } = useFirestore();

  const contactId = contact?.id;
  const listingIds = linkedListingIdsOf(contact);
  // Effects key on the ids' VALUE: the document is a new object on every snapshot.
  const listingIdsKey = listingIds.join("\n");

  const [ownByListing, setOwnByListing] = useState<Record<string, UserNote[]>>({});
  const [orgByListing, setOrgByListing] = useState<Record<string, DisplayUserNote[]>>({});
  const [labels, setLabels] = useState<Record<string, string | undefined>>({});

  // Mine — one document per listing, by its deterministic id (how the property's notes are
  // written: `useListingNotes`).
  useEffect(() => {
    setOwnByListing((previous) => (Object.keys(previous).length ? {} : previous));
    const ids = listingIdsKey ? listingIdsKey.split("\n") : [];
    if (!userEmail || ids.length === 0) return undefined;
    const unsubscribes = ids.map((listingId) =>
      db
        .collection(USER_NOTES_COLLECTION_NAME)
        .doc(userNotesDocId({ userId: userEmail, listingId }))
        .onSnapshot(
          (snapshot) => {
            const notes = snapshot.exists ? (snapshot.data() as UserNotesDocument).notes ?? [] : [];
            setOwnByListing((previous) => ({ ...previous, [listingId]: notes }));
          },
          (error) => console.error("usePropertyNotes: own property notes failed", error)
        )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [userEmail, listingIdsKey]);

  // Teammates' — the office-shared notes on each listing, re-filtered per note (the document's
  // `organizationIds` is a union over shared and private notes), exactly as `useListingNotes`.
  useEffect(() => {
    setOrgByListing((previous) => (Object.keys(previous).length ? {} : previous));
    const ids = listingIdsKey ? listingIdsKey.split("\n") : [];
    if (!userEmail || !organizationId || ids.length === 0) return undefined;
    const unsubscribes = ids.map((listingId) =>
      db
        .collection(USER_NOTES_COLLECTION_NAME)
        .where("listingId", "==", listingId)
        .where("organizationIds", "array-contains", organizationId)
        .onSnapshot(
          (snapshot) => {
            const collected: DisplayUserNote[] = [];
            snapshot.docs.forEach((doc) => {
              const data = doc.data() as UserNotesDocument;
              if (data.userId === userEmail) return; // mine are read above
              for (const note of data.notes ?? []) {
                if (!(note.organizationIds ?? []).includes(organizationId)) continue;
                collected.push({ ...note, noteTime: parseNoteTime(note.noteTime), isMine: false, authorEmail: data.userId });
              }
            });
            setOrgByListing((previous) => ({ ...previous, [listingId]: collected }));
          },
          (error) => console.error("usePropertyNotes: office property notes failed", error)
        )
    );
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [userEmail, organizationId, listingIdsKey]);

  // Which property each note is about — the listing's address, once. An ad taken down has no
  // document any more; its notes still show, labelled only "on the property".
  useEffect(() => {
    const ids = listingIdsKey ? listingIdsKey.split("\n") : [];
    if (ids.length === 0) return undefined;
    let cancelled = false;
    fetchListingsByIds(ids)
      .then((hits) => {
        if (cancelled) return;
        const next: Record<string, string | undefined> = {};
        for (const hit of hits) next[hit.objectID ?? hit.id ?? ""] = propertyLabelOf(hit.address);
        setLabels(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [listingIdsKey]);

  const notes = useMemo(() => {
    const nameByEmail = new Map<string, string>();
    for (const orgUser of organizationUsers ?? []) {
      if (orgUser.email) nameByEmail.set(orgUser.email, orgUser.name || orgUser.email);
    }
    return buildPropertyNotes({
      listingIds: listingIdsKey ? listingIdsKey.split("\n") : [],
      ownByListing,
      orgByListing,
      labels,
      userEmail: userEmail ?? "",
      nameByEmail
    });
  }, [listingIdsKey, ownByListing, orgByListing, labels, organizationUsers, userEmail]);

  useEffect(() => {
    if (contactId) setCached(propertyNotesCacheKey(contactId), notes);
  }, [contactId, notes]);

  return notes;
}
